// AI Recording Studio Pro — Dashboard
// Professional dashboard with stats, recent projects, recording history
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useRecordingStore } from "@/stores/recording-store";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import {
  MonitorPlay,
  FolderKanban,
  Clock,
  HardDrive,
  Star,
  Download,
  Plus,
  ChevronRight,
  Play,
  Trash2,
  Pencil,
  Film,
  Brain,
  Terminal,
  Sparkles,
  TrendingUp,
  Calendar,
  FileVideo,
} from "lucide-react";
import { toast } from "sonner";

const formatDuration = (seconds) => {
  if (!seconds) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatSize = (bytes) => {
  if (!bytes) return "0 MB";
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

const STATUS_CONFIG = {
  draft: { label: "Brouillon", color: "bg-slate-500/10 text-slate-600 border-slate-300" },
  recording: { label: "En cours", color: "bg-amber-500/10 text-amber-600 border-amber-300" },
  editing: { label: "Édition", color: "bg-blue-500/10 text-blue-600 border-blue-300" },
  exporting: { label: "Export", color: "bg-purple-500/10 text-purple-600 border-purple-300" },
  completed: { label: "Terminé", color: "bg-emerald-500/10 text-emerald-600 border-emerald-300" },
  archived: { label: "Archivé", color: "bg-gray-500/10 text-gray-500 border-gray-300" },
};

// ── Stat Card Widget ──
const StatCard = ({ icon: Icon, label, value, subtitle, gradient, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Card className="relative overflow-hidden group hover:shadow-md transition-shadow duration-300">
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl shadow-sm group-hover:scale-110 transition-transform duration-300"
            style={{ background: gradient }}
          >
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
      <div
        className="absolute bottom-0 left-0 right-0 h-0.5 opacity-60"
        style={{ background: gradient }}
      />
    </Card>
  </motion.div>
);

// ── Quick Action Button ──
const QuickAction = ({ icon: Icon, label, description, onClick, gradient, delay = 0 }) => (
  <motion.button
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, delay }}
    onClick={onClick}
    className="flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:bg-muted/30 hover:shadow-md transition-all duration-200 text-left group w-full"
  >
    <div
      className="flex h-11 w-11 items-center justify-center rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
      style={{ background: gradient, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
    >
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold truncate">{label}</p>
      <p className="text-[11px] text-muted-foreground truncate">{description}</p>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
  </motion.button>
);

export default function RecordingStudioDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem("token");

  const {
    projects, fetchProjects, projectsLoading,
    stats, fetchStats, statsLoading,
    createProject,
    updateProject,
    deleteProject,
  } = useRecordingStore();

  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "", theme: "" });
  const [creating, setCreating] = useState(false);

  // Load data on mount
  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      await Promise.all([
        fetchProjects(token),
        fetchStats(token),
      ]);
    } catch {
      // Fail silently for dashboard
    }
  }, [token, fetchProjects, fetchStats]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create project
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("Nom du projet requis");
      return;
    }
    setCreating(true);
    try {
      const project = await createProject(token, newProject);
      toast.success(`Projet "${project.name}" créé`);
      setShowNewProject(false);
      setNewProject({ name: "", description: "", theme: "" });
    } catch (err) {
      toast.error(err.message || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  // Toggle favorite
  const handleToggleFavorite = async (e, project) => {
    e.stopPropagation();
    try {
      await updateProject(token, project.id, { is_favorite: !project.is_favorite });
      toast.success(project.is_favorite ? "Retiré des favoris" : "Ajouté aux favoris");
    } catch {
      toast.error("Erreur");
    }
  };

  // Delete project
  const handleDeleteProject = async (e, project) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer le projet "${project.name}" ?`)) return;
    try {
      await deleteProject(token, project.id);
      toast.success("Projet supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const recentProjects = projects.slice(0, 6);
  const favoriteProjects = projects.filter((p) => p.is_favorite).slice(0, 3);

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 space-y-8 max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                Recording Studio
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Bienvenue, {user?.first_name}. Créez des vidéos de formation professionnelles.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewProject(true)}
                className="gap-1.5"
                data-testid="new-project-btn"
              >
                <Plus className="h-4 w-4" />
                Nouveau projet
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/studio/record")}
                className="gap-1.5"
                style={{
                  background: "linear-gradient(135deg, #ef4444, #f97316)",
                  border: "none",
                }}
                data-testid="start-recording-btn"
              >
                <MonitorPlay className="h-4 w-4" />
                Enregistrer
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard
            icon={FolderKanban}
            label="Projets"
            value={statsLoading ? "..." : stats?.total_projects || 0}
            gradient="linear-gradient(135deg, #6366f1, #8b5cf6)"
            delay={0}
          />
          <StatCard
            icon={FileVideo}
            label="Enregistrements"
            value={statsLoading ? "..." : stats?.total_recordings || 0}
            gradient="linear-gradient(135deg, #ef4444, #f97316)"
            delay={0.05}
          />
          <StatCard
            icon={Clock}
            label="Durée totale"
            value={statsLoading ? "..." : formatDuration(stats?.total_duration_seconds || 0)}
            gradient="linear-gradient(135deg, #3b82f6, #06b6d4)"
            delay={0.1}
          />
          <StatCard
            icon={HardDrive}
            label="Stockage"
            value={statsLoading ? "..." : formatSize(stats?.total_size_bytes || 0)}
            gradient="linear-gradient(135deg, #22c55e, #10b981)"
            delay={0.15}
          />
          <StatCard
            icon={Star}
            label="Favoris"
            value={statsLoading ? "..." : stats?.favorite_projects || 0}
            gradient="linear-gradient(135deg, #eab308, #f97316)"
            delay={0.2}
          />
          <StatCard
            icon={Download}
            label="Exports"
            value={statsLoading ? "..." : stats?.completed_exports || 0}
            gradient="linear-gradient(135deg, #8b5cf6, #ec4899)"
            delay={0.25}
          />
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Actions rapides
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickAction
              icon={MonitorPlay}
              label="Nouvel enregistrement"
              description="Écran, webcam, audio"
              onClick={() => navigate("/studio/record")}
              gradient="linear-gradient(135deg, #ef4444, #f97316)"
              delay={0.1}
            />
            <QuickAction
              icon={Plus}
              label="Nouveau projet"
              description="Organiser vos vidéos"
              onClick={() => setShowNewProject(true)}
              gradient="linear-gradient(135deg, #6366f1, #8b5cf6)"
              delay={0.15}
            />
            <QuickAction
              icon={Brain}
              label="AI Script Generator"
              description="Générer un script IA"
              onClick={() => navigate("/studio/ai")}
              gradient="linear-gradient(135deg, #3b82f6, #06b6d4)"
              delay={0.2}
            />
            <QuickAction
              icon={Terminal}
              label="SSH Manager"
              description="Connexions serveur"
              onClick={() => navigate("/studio/ssh")}
              gradient="linear-gradient(135deg, #22c55e, #10b981)"
              delay={0.25}
            />
          </div>
        </div>

        {/* Favorite Projects */}
        {favoriteProjects.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              Projets favoris
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {favoriteProjects.map((project, i) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  delay={i * 0.05}
                  onNavigate={() => navigate(`/studio/projects`)}
                  onToggleFavorite={(e) => handleToggleFavorite(e, project)}
                  onDelete={(e) => handleDeleteProject(e, project)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Recent Projects */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Projets récents
            </h2>
            {projects.length > 6 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/studio/projects")}
                className="text-xs gap-1"
              >
                Voir tout
                <ChevronRight className="h-3 w-3" />
              </Button>
            )}
          </div>

          {projectsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-5 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-3 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : recentProjects.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentProjects.map((project, i) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  delay={i * 0.05}
                  onNavigate={() => navigate(`/studio/projects`)}
                  onToggleFavorite={(e) => handleToggleFavorite(e, project)}
                  onDelete={(e) => handleDeleteProject(e, project)}
                />
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div
                  className="h-14 w-14 rounded-2xl flex items-center justify-center mb-4"
                  style={{
                    background: "linear-gradient(135deg, #ef4444 0%, #f97316 50%, #eab308 100%)",
                    boxShadow: "0 8px 24px rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <Film className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg">Commencez à enregistrer</h3>
                <p className="text-sm text-muted-foreground text-center mt-1 max-w-md">
                  Créez votre premier projet et enregistrez des vidéos de formation professionnelles
                  directement depuis votre navigateur.
                </p>
                <div className="flex gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewProject(true)}
                    className="gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    Créer un projet
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate("/studio/record")}
                    className="gap-1.5"
                    style={{ background: "linear-gradient(135deg, #ef4444, #f97316)", border: "none" }}
                  >
                    <Play className="h-4 w-4" />
                    Enregistrer maintenant
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Sessions */}
        {stats?.recent_sessions?.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Dernières sessions
            </h2>
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-border/50">
                  {stats.recent_sessions.slice(0, 5).map((session, i) => (
                    <motion.div
                      key={session.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                    >
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileVideo className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{session.original_filename || session.filename}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatDuration(session.duration_seconds)} · {formatSize(session.file_size_bytes)}
                        </p>
                      </div>
                      <span className="text-[11px] text-muted-foreground flex-shrink-0">
                        {formatDate(session.created_at)}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* New Project Dialog */}
        <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderKanban className="h-5 w-5 text-primary" />
                Nouveau projet
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Nom du projet *
                </label>
                <Input
                  placeholder="Ex: Formation Kubernetes HA"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Description
                </label>
                <Textarea
                  placeholder="Décrivez le contenu de la formation..."
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Thème
                </label>
                <Input
                  placeholder="Ex: DevOps, Kubernetes, Docker..."
                  value={newProject.theme}
                  onChange={(e) => setNewProject({ ...newProject, theme: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewProject(false)} size="sm">
                Annuler
              </Button>
              <Button
                onClick={handleCreateProject}
                disabled={creating || !newProject.name.trim()}
                size="sm"
                style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none" }}
                data-testid="create-project-submit"
              >
                {creating ? "Création..." : "Créer le projet"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </RecordingStudioLayout>
  );
}

// ── Project Card Component ──
function ProjectCard({ project, delay, onNavigate, onToggleFavorite, onDelete }) {
  const statusCfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.draft;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Card
        className="group hover:shadow-md hover:border-primary/20 transition-all duration-200 cursor-pointer"
        onClick={onNavigate}
      >
        <CardContent className="pt-4 pb-3 px-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold truncate">{project.name}</h3>
              {project.theme && (
                <p className="text-[11px] text-muted-foreground truncate mt-0.5">{project.theme}</p>
              )}
            </div>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 flex-shrink-0 ${statusCfg.color}`}>
              {statusCfg.label}
            </Badge>
          </div>

          {project.description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{project.description}</p>
          )}

          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <FileVideo className="h-3 w-3" />
                {project.recordings_count}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(project.total_duration_seconds)}
              </span>
              <span className="flex items-center gap-1">
                <HardDrive className="h-3 w-3" />
                {formatSize(project.total_size_bytes)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/30">
            <span className="text-[10px] text-muted-foreground">
              {formatDate(project.updated_at)}
            </span>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onToggleFavorite}
                title={project.is_favorite ? "Retirer des favoris" : "Ajouter aux favoris"}
              >
                <Star className={`h-3.5 w-3.5 ${project.is_favorite ? "fill-amber-400 text-amber-400" : ""}`} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={onDelete}
                title="Supprimer"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
